import { FC } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors, spacing, fontStyles } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

// Correlation Renderer - Display correlation matrix analysis
export const CorrelationRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Analyzing correlation...
      </div>
    );
  }

  const correlationMatrix = output.correlation_matrix;
  const annualizedReturn = output.annualized_return;
  const analysis = output.analysis;
  const insights = output.insights;

  if (!correlationMatrix) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Correlation analysis completed
      </div>
    );
  }

  // Build correlation matrix table
  const symbols = Object.keys(correlationMatrix);
  let tableMarkdown = '## Correlation Matrix\n\n';

  // Table header
  tableMarkdown += '|   |' + symbols.map((s) => ` ${s} |`).join('') + '\n';
  tableMarkdown += '|---|' + symbols.map(() => '-----|').join('') + '\n';

  // Table rows
  symbols.forEach((rowSymbol) => {
    tableMarkdown += `| **${rowSymbol}** |`;
    symbols.forEach((colSymbol) => {
      const correlation = correlationMatrix[rowSymbol][colSymbol];
      tableMarkdown += ` ${correlation.toFixed(2)} |`;
    });
    tableMarkdown += '\n';
  });

  // High correlation pairs
  const highCorrelationPairs: { pair: string; correlation: number }[] = [];
  symbols.forEach((symbol1) => {
    symbols.forEach((symbol2) => {
      if (symbol1 < symbol2) {
        // Avoid duplicates
        const correlation = correlationMatrix[symbol1][symbol2];
        if (Math.abs(correlation) > 0.7) {
          highCorrelationPairs.push({
            pair: `${symbol1}-${symbol2}`,
            correlation: correlation,
          });
        }
      }
    });
  });

  if (highCorrelationPairs.length > 0) {
    tableMarkdown += '\n## High Correlation Pairs (>0.7)\n\n';
    highCorrelationPairs
      .sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation))
      .forEach((pair) => {
        tableMarkdown += `- **${pair.pair}**: ${pair.correlation.toFixed(2)}\n`;
      });
  }

  // Annualized returns
  if (annualizedReturn) {
    tableMarkdown += '\n## Annualized Returns\n\n';
    const returns = Object.entries(annualizedReturn)
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .map(([symbol, returnVal]) => `**${symbol}**: ${((returnVal as number) * 100).toFixed(1)}%`)
      .join(' | ');
    tableMarkdown += returns + '\n';
  }

  // Analysis
  if (analysis) {
    tableMarkdown += '\n## Analysis\n\n';
    tableMarkdown += analysis + '\n';
  }

  // Insights
  if (insights && insights !== analysis) {
    tableMarkdown += '\n## Key Insights\n\n';
    tableMarkdown += insights + '\n';
  }

  return (
    <div
      css={css`
        color: ${colors.liquorice};
        font-size: 14px;
        line-height: 1.4;

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }

        /* Correlation value color coding */
        & td {
          &:not(:first-child) {
            font-weight: 500;
          }
        }

        /* Style correlation values based on strength */
        & table td {
          position: relative;
        }

        /* Markdown styles matching other renderers */
        & p {
          margin: 0 0 ${spacing.centi} 0;
          &:last-child {
            margin-bottom: 0;
          }
        }
        & h1 {
          ${fontStyles.lion}
          margin: 0 0 ${spacing.centi} 0;
        }
        & h2 {
          ${fontStyles.dog}
          margin: 0 0 ${spacing.centi} 0;
        }
        & h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 ${spacing.milli} 0;
        }
        & ul,
        & ol {
          margin: ${spacing.centi} 0;
          padding-left: ${spacing.regular};
        }
        & li {
          margin: ${spacing.milli} 0;
        }

        /* Table styles */
        & .table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: ${spacing.deci} 0;
          border-radius: 8px;
          border: 1px solid ${colors.cloud};
        }

        & table {
          border-collapse: collapse;
          width: 100%;
          display: table;
          font-size: 0.9em;
          margin: 0;
        }

        & th,
        & td {
          border: none;
          border-bottom: 1px solid ${colors.cloud};
          padding: ${spacing.deci};
          text-align: center;
          vertical-align: top;
          white-space: nowrap;
          line-height: 1.5;
        }

        & th {
          background-color: rgba(0, 0, 0, 0.05);
          font-weight: 600;
          border-bottom: 2px solid ${colors.cloud};
        }

        & tr:nth-of-type(even) {
          background-color: rgba(0, 0, 0, 0.02);
        }

        & tr:last-child td {
          border-bottom: none;
        }

        html[data-theme='dark'] & {
          & .table-wrapper {
            border-color: ${colors.tungsten};
          }

          & th,
          & td {
            border-color: ${colors.tungsten};
          }

          & th {
            background-color: rgba(255, 255, 255, 0.05);
            border-bottom-color: ${colors.tungsten};
          }

          & tr:nth-of-type(even) {
            background-color: rgba(255, 255, 255, 0.02);
          }
        }
      `}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children, ...props }) => (
            <div className="table-wrapper">
              <table {...props}>{children}</table>
            </div>
          ),
          td: ({ children, ...props }) => {
            // Color code correlation values
            const value = typeof children === 'string' ? parseFloat(children) : NaN;
            if (!isNaN(value) && value !== 1.0) {
              const color =
                value > 0.7
                  ? colors.leaf // Strong positive (green)
                  : value < -0.5
                    ? colors.berry // Strong negative (red)
                    : undefined; // Default color

              return (
                <td {...props}>
                  <span style={{ color }}>{children}</span>
                </td>
              );
            }
            return <td {...props}>{children}</td>;
          },
          th: ({ children, ...props }) => <th {...props}>{children}</th>,
        }}
      >
        {cleanMarkdownContent(tableMarkdown)}
      </ReactMarkdown>
    </div>
  );
};
